import { HsaLmnLookupEnvironment } from "@redotech/crdb-enums/conversion/hsa-lmn-lookup-environment";
import { HsaLmnLookupQueryKind } from "@redotech/crdb-enums/conversion/hsa-lmn-lookup-query-kind";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";

/** What the letter provider said about one order's letters. */
export enum LmnLetterLookupOutcome {
  /** The order has letters; they are listed. */
  AVAILABLE = "available",
  /** No letter was ever requested for the order. */
  NONE = "none",
  /** The letter service could not be read; the reason says why. */
  UNAVAILABLE = "unavailable",
}

/** The most orders one lookup returns; more matches set `truncated`. */
export const LMN_LETTER_LOOKUP_MAX_ORDERS = 20;

const MAX_EMAIL_LENGTH = 254;
const MAX_MERCHANT_ORDER_REFERENCE_LENGTH = 100;

/** Gale order id after normalization: `ord_` + an uppercase Crockford ULID. */
export const LMN_ORDER_ID_PATTERN = /^ord_[0-9A-HJKMNP-TV-Z]{26}$/;
/** Gale checkout session id, prefix optional, after lowercasing. */
export const LMN_CHECKOUT_ID_PATTERN = /^(?:cs_)?[0-9a-f]{32}$/;

const lmnLetterLookupQuerySchema_ = z
  .object({
    kind: createEnum(HsaLmnLookupQueryKind),
    value: z.string().trim().min(1),
    environment: createEnum(HsaLmnLookupEnvironment),
  })
  .superRefine((query, context) => {
    const problem = lmnLetterLookupValueProblem(query.kind, query.value);
    if (problem) {
      context.addIssue({ code: "custom", path: ["value"], message: problem });
    }
  });
/**
 * A lookup as the caller sends it. `value` must already be normalized for its
 * kind (`normalizeLmnLetterLookupValue`), so a lowercase order id or an
 * uppercase checkout id is rejected here rather than missed upstream.
 */
export const lmnLetterLookupQuerySchema: z.ZodType<LmnLetterLookupQuery> =
  lmnLetterLookupQuerySchema_;
export interface LmnLetterLookupQuery extends z.infer<
  typeof lmnLetterLookupQuerySchema_
> {}

const lmnLetterSchema_ = z.object({
  productId: z.string().nullable(),
  productName: z.string().nullable(),
  /** Beluga visit `masterId` (lmn-apis `belugaReferenceId`): the id support
   * quotes to Beluga for an amendment. Null when the provider has none. */
  masterId: z.string().nullable(),
  /** `pending | approved | failed | disqualified`, or a newer state passed
   * through as is. */
  state: z.string(),
});
export const lmnLetterSchema: z.ZodType<LmnLetter> = lmnLetterSchema_;
export interface LmnLetter extends z.infer<typeof lmnLetterSchema_> {}

const lmnLetterOrderLmnSchema_ = z.discriminatedUnion("outcome", [
  z.object({
    outcome: z.literal(LmnLetterLookupOutcome.AVAILABLE),
    orderState: z.string(),
    /** `new`, or `reused` when an earlier letter was reused (its master id is
     * then the original letter's). */
    issuanceType: z.string().nullable(),
    letters: z.array(lmnLetterSchema),
  }),
  z.object({ outcome: z.literal(LmnLetterLookupOutcome.NONE) }),
  z.object({
    outcome: z.literal(LmnLetterLookupOutcome.UNAVAILABLE),
    reason: z.string(),
  }),
]);
export type LmnLetterOrderLmn = z.infer<typeof lmnLetterOrderLmnSchema_>;

const lmnLetterOrderSchema_ = z.object({
  orderId: z.string(),
  checkoutSessionId: z.string().nullable(),
  merchantOrderReference: z.string().nullable(),
  merchant: z
    .object({ id: z.string(), brandName: z.string().nullable() })
    .nullable(),
  orderStatus: z.string(),
  testMode: z.boolean(),
  /** ISO 8601. */
  createdAt: z.string().nullable(),
  lmn: lmnLetterOrderLmnSchema_,
});
export const lmnLetterOrderSchema: z.ZodType<LmnLetterOrder> =
  lmnLetterOrderSchema_;
export interface LmnLetterOrder extends z.infer<typeof lmnLetterOrderSchema_> {}

const lmnLetterLookupResultSchema_ = z.object({
  /** Newest first, at most `LMN_LETTER_LOOKUP_MAX_ORDERS`. */
  orders: z.array(lmnLetterOrderSchema).max(LMN_LETTER_LOOKUP_MAX_ORDERS),
  /** More orders matched than were returned. */
  truncated: z.boolean(),
});
export const lmnLetterLookupResultSchema: z.ZodType<LmnLetterLookupResult> =
  lmnLetterLookupResultSchema_;
export interface LmnLetterLookupResult extends z.infer<
  typeof lmnLetterLookupResultSchema_
> {}

/** Why a normalized value cannot be a key of its kind, or null when it can. */
export function lmnLetterLookupValueProblem(
  kind: HsaLmnLookupQueryKind,
  value: string,
): string | null {
  switch (kind) {
    case HsaLmnLookupQueryKind.Email:
      if (value.length > MAX_EMAIL_LENGTH) {
        return `An email is at most ${MAX_EMAIL_LENGTH} characters`;
      }
      return value.includes("@") ? null : "An email contains @";
    case HsaLmnLookupQueryKind.OrderId:
      return LMN_ORDER_ID_PATTERN.test(value)
        ? null
        : "An order id is ord_ followed by 26 letters and digits";
    case HsaLmnLookupQueryKind.CheckoutId:
      return LMN_CHECKOUT_ID_PATTERN.test(value)
        ? null
        : "A checkout id is cs_ followed by 32 hex characters";
    case HsaLmnLookupQueryKind.MerchantOrderReference:
      return value.length > MAX_MERCHANT_ORDER_REFERENCE_LENGTH
        ? `A merchant order reference is at most ${MAX_MERCHANT_ORDER_REFERENCE_LENGTH} characters`
        : null;
  }
}
